<template>
  <div
    class="flex bg-white rounded-lg border-3 border-b-0 h-[34px]"
    :style="{ borderColor: color }">
    <BaseTooltip
      v-for="(option, index) in optionsList"
      :key="index"
      popper-class="z-[9999]"
      placement="top"
      :class="getButtonClass(option)"
      @click="!option.disabled && option.onClick()">
      <template #content>
        <span>{{ option.label }}</span>
      </template>
      <template #trigger>
        <div
          @mouseenter="
            option.label === deleteLabel && emit('delete:hover', true)
          "
          @mouseleave="
            option.label === deleteLabel && emit('delete:hover', false)
          ">
          <component
            :is="option.icon"
            :size="16"
            :class="getIconClass(option)"/>
        </div>
      </template>
    </BaseTooltip>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { PropType, FunctionalComponent } from 'vue';
import { PairingItemFormat } from '@amit/types';
import { BaseTooltip } from '@nwire/amit-design-system';
import { Type, Image, ArrowDownUp, ArrowUpDown, X } from 'lucide-vue-next';
import type { LucideProps } from 'lucide-vue-next';

interface IconButtonOption {
  label: string;
  icon: FunctionalComponent<LucideProps>;
  onClick: () => void;
  active: boolean;
  disabled: boolean;
}

const props = defineProps({
  format: {
    type: String as PropType<PairingItemFormat>,
    default: PairingItemFormat.TextImage,
  },
  color: {
    type: String,
    default: '',
  },
  canDelete: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  'update:format': [PairingItemFormat];
  'delete:item': [];
  'delete:hover': [boolean];
}>();

const { t } = useI18n();

const deleteLabel = t('blocksSettings.pairing.delete');
const imageLabel = t('blocksSettings.pairing.image');
const textLabel = t('blocksSettings.pairing.text');
const orderLabel = t('blocksSettings.pairing.change_order');

const state = ref({
  isText: props.format.includes('text'),
  isImage: props.format.includes('image'),
  isTextFirst: props.format === PairingItemFormat.TextImage,
});

const activeFormat = computed<PairingItemFormat>(() => {
  if (state.value.isText && state.value.isImage) {
    return state.value.isTextFirst
      ? PairingItemFormat.TextImage
      : PairingItemFormat.ImageText;
  }
  return state.value.isImage ? PairingItemFormat.Image : PairingItemFormat.Text;
});

watch(activeFormat, newFormat => {
  emit('update:format', newFormat);
});

const showChangeOrder = computed(
  () => state.value.isText && state.value.isImage,
);

const optionsList = computed<IconButtonOption[]>(() => {
  const options: IconButtonOption[] = [];

  if (showChangeOrder.value) {
    options.push({
      label: orderLabel,
      icon: state.value.isTextFirst ? ArrowDownUp : ArrowUpDown,
      onClick: () => (state.value.isTextFirst = !state.value.isTextFirst),
      active: false,
      disabled: false,
    });
  }

  options.push(
    {
      label: imageLabel,
      icon: Image,
      onClick: () => (state.value.isImage = !state.value.isImage),
      active: state.value.isImage,
      disabled: state.value.isImage && !state.value.isText,
    },
    {
      label: textLabel,
      icon: Type,
      onClick: () => (state.value.isText = !state.value.isText),
      active: state.value.isText,
      disabled: state.value.isText && !state.value.isImage,
    },
  );

  if (props.canDelete) {
    options.push({
      label: deleteLabel,
      icon: X,
      onClick: () => emit('delete:item'),
      active: false,
      disabled: false,
    });
  }

  return options;
});

const getIconClass = (option: IconButtonOption) => {
  if (option.label === deleteLabel) {
    return 'stroke-secondary-800 hover:stroke-red-500 active:stroke-red-500 transition-colors duration-150';
  } else if (option.label === orderLabel) {
    return 'stroke-secondary-800';
  }
};

const getButtonClass = (option: IconButtonOption) => {
  const itemClass = [
    'flex grow items-center justify-center first:rounded-s last:rounded-e',
  ];

  if (option.active) {
    itemClass.push('bg-primary-600 text-white');
  } else if (option.label === textLabel || option.label === imageLabel) {
    itemClass.push('hover:bg-primary-600 hover:text-white text-secondary-800');
  }

  if (option.disabled) {
    itemClass.push('pointer-events-none cursor-none');
  } else {
    itemClass.push('cursor-pointer');
  }
  return itemClass;
};
</script>
